import {
  EXPECTED_COLUMN_FIELDS,
  REQUIRED_COLUMN_GROUPS,
} from '@pages/UploadClaimsFile/constants';
import { FileMatch, FileMatches } from '@pages/UploadClaimsFile/types';
import {
  FieldMatchStatus,
  FieldStatusEntry,
  FieldStatusSections,
} from './types';

function getEntryStatus(fileMatch: FileMatch | undefined): FieldMatchStatus {
  if (!fileMatch?.matched) return 'pending';
  if (!fileMatch.errorMsg) return 'completed';
  // Only a required field's error blocks submission; recommended/optional fields warn instead.
  return fileMatch.required ? 'error' : 'warning';
}

function getFieldLabel(field: { labels: string[] }): string {
  return (
    field.labels.find((label) => label.includes(' ') && !label.includes('_')) ||
    field.labels.find(
      (label) => label[0] === label[0]?.toUpperCase() && !label.includes('_'),
    ) ||
    field.labels[0]
  );
}

function pushEntry(
  sections: FieldStatusSections,
  entry: FieldStatusEntry,
  { required, recommended }: { required: boolean; recommended: boolean },
): void {
  if (required) {
    sections.required.push(entry);
  } else if (recommended) {
    sections.recommended.push(entry);
  } else {
    sections.optional.push(entry);
  }
}

export function buildFieldStatusSections(
  fileMatches: FileMatches,
): FieldStatusSections {
  const sections: FieldStatusSections = {
    required: [],
    recommended: [],
    optional: [],
  };

  const groupedFieldKeys = new Set(
    REQUIRED_COLUMN_GROUPS.flatMap((group) => group.keys),
  );

  REQUIRED_COLUMN_GROUPS.forEach((group) => {
    const match = fileMatches.find(
      (fileMatch) => (fileMatch.field as string) === group.groupKey,
    );

    pushEntry(
      sections,
      {
        key: group.groupKey,
        label: group.label,
        status: getEntryStatus(match),
        matchKeys: group.keys,
      },
      { required: group.required, recommended: group.recommended },
    );
  });

  EXPECTED_COLUMN_FIELDS.forEach((field) => {
    if (groupedFieldKeys.has(field.key)) return;

    const match = fileMatches.find(
      (fileMatch) => fileMatch.field === field.key,
    );

    pushEntry(
      sections,
      {
        key: field.key,
        label: getFieldLabel(field),
        status: getEntryStatus(match),
        matchKeys: [field.key],
      },
      { required: field.required, recommended: field.recommended },
    );
  });

  return sections;
}
